import { ChevronRight, Search } from "lucide-react"

import { CohortAvatar } from "components/mentorship/CohortExperience"
import { Input } from "components/ui/input"
import type { ReactNode } from "react"
import { cn } from "lib/utils"
import { getCohortRoleLabels, type CohortRoleLabelSource } from "utils/cohortRoleLabels"
import type { Person } from "./types"

const rosterCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })

const normalizeRosterSortValue = (value?: string | null) => value?.trim() || ""

type RosterCardProps = {
  title?: string
  mobileEyebrow?: string
  mobileTitle?: string
  roster: Person[]
  searchValue: string
  onSearchChange: (value: string) => void
  onSelectPerson: (person: Person) => void
  emptyState?: ReactNode
  previewLimit?: number
  onOpenAll?: () => void
  showProgress?: boolean
  groupByRole?: boolean
  roleLabels?: CohortRoleLabelSource
  relationshipPriority?: "mentor" | "mentees"
  relationshipFacts?: Readonly<Record<string, string>>
  className?: string
}

export function RosterCard({
  title = "Roster",
  mobileEyebrow,
  mobileTitle,
  roster,
  searchValue,
  onSearchChange,
  onSelectPerson,
  emptyState,
  previewLimit,
  onOpenAll,
  showProgress = false,
  groupByRole = false,
  roleLabels,
  relationshipPriority,
  relationshipFacts = {},
  className,
}: RosterCardProps) {
  const getRoleKey = (person: Person) => {
    if (person.roleKey) return person.roleKey
    const role = person.role.toLowerCase()
    if (role.includes("mentor")) return "mentor"
    if (role.includes("director")) return "cohort_director"
    if (role.includes("admin")) return "cohort_admin"
    return "mentee"
  }
  const allVisibleRoster = roster
    .filter((person) => getRoleKey(person) !== "cohort_admin")
    .sort((first, second) => {
      const relationshipRank = (person: Person) => {
        if (relationshipPriority === "mentor") return person.isMyMentor ? 0 : 1
        if (relationshipPriority === "mentees") return person.isMyMentee ? 0 : 1
        return 0
      }
      const relationshipComparison = relationshipRank(first) - relationshipRank(second)
      if (relationshipComparison !== 0) return relationshipComparison

      const roleComparison = rosterCollator.compare(
        normalizeRosterSortValue(first.role),
        normalizeRosterSortValue(second.role),
      )

      if (roleComparison !== 0) return roleComparison

      return rosterCollator.compare(normalizeRosterSortValue(first.name), normalizeRosterSortValue(second.name))
    })
  const visibleRoster = typeof previewLimit === "number" ? allVisibleRoster.slice(0, previewLimit) : allVisibleRoster
  const configuredRoleLabels = getCohortRoleLabels(roleLabels)
  const getRoleLabel = (person: Person) => {
    if (!roleLabels) return person.role
    const roleKey = getRoleKey(person)
    if (roleKey === "mentor") return configuredRoleLabels.mentorSingular
    if (roleKey === "mentee") return configuredRoleLabels.menteeSingular
    return person.role
  }
  const mentorRelationshipLabel = roleLabels ? configuredRoleLabels.mentorSingular.toLowerCase() : "mentor"
  const menteeRelationshipLabel = roleLabels ? configuredRoleLabels.menteeSingular.toLowerCase() : "mentee"
  const relationshipPeople = allVisibleRoster.filter((person) => (
    relationshipPriority === "mentor" ? person.isMyMentor : relationshipPriority === "mentees" ? person.isMyMentee : false
  ))
  const remainingPeople = relationshipPriority
    ? allVisibleRoster.filter((person) => !relationshipPeople.some((relationshipPerson) => relationshipPerson.id === person.id))
    : allVisibleRoster
  const roleGroups = [
    ...(relationshipPeople.length ? [{
      key: "relationship",
      label: relationshipPriority === "mentor"
        ? `Your ${configuredRoleLabels.mentorSingular}`
        : `Your ${configuredRoleLabels.menteePlural} · ${relationshipPeople.length}`,
      people: relationshipPeople,
    }] : []),
    {
      key: "mentor",
      label: configuredRoleLabels.mentorPlural,
      people: remainingPeople.filter((person) => getRoleKey(person) === "mentor"),
    },
    {
      key: "mentee",
      label: configuredRoleLabels.menteePlural,
      people: remainingPeople.filter((person) => getRoleKey(person) === "mentee"),
    },
    {
      key: "cohort-team",
      label: "Cohort team",
      people: remainingPeople.filter((person) => !["mentor", "mentee"].includes(getRoleKey(person))),
    },
  ].filter((group) => group.people.length)

  const renderPersonRow = (person: Person, index?: number) => {
    const roleKey = getRoleKey(person)
    const relationshipFact = relationshipFacts[String(person.id)]
    return (
      <button
        type="button"
        key={person.id}
        onClick={() => onSelectPerson(person)}
        className={cn(
          "cohort-ruled-row cohort-row-action flex min-h-[64px] w-full min-w-0 items-center gap-3 py-[11px] text-left sm:py-3.5",
          typeof previewLimit === "number" && typeof index === "number" && index >= 3 && "hidden sm:flex",
        )}
      >
        <CohortAvatar person={person} size="lg" tone={roleKey === "mentor" ? "mentor" : "default"} />
        <div className="min-w-0 flex-1">
          <h3 className="break-words text-base font-medium text-[var(--cohort-ink)]">{person.name}</h3>
          <p className={cn(
            "mt-0.5 break-words text-sm text-[var(--cohort-ink-muted)]",
            person.isMyMentor && "text-[var(--cohort-orange-ink)]",
          )}>
            {relationshipFact || <>{getRoleLabel(person)}{person.isMyMentor ? ` · your ${mentorRelationshipLabel}` : person.isMyMentee ? ` · your ${menteeRelationshipLabel}` : ""}</>}
          </p>
        </div>
        {showProgress && person.taskCompletion.total ? (
          <span className="shrink-0 text-sm text-[var(--cohort-ink-muted)]">{person.taskCompletion.completed} of {person.taskCompletion.total} tasks</span>
        ) : null}
        <ChevronRight className="h-4 w-4 shrink-0 text-[var(--cohort-ink-muted)]" />
      </button>
    )
  }

  return (
    <section id="admin-roster" className={cn("cohort-open-section min-w-0", className)} aria-labelledby="cohort-roster-heading">
      {mobileEyebrow ? <p className="cohort-section-eyebrow lg:hidden">{mobileEyebrow}</p> : null}
      <div className="cohort-card-heading">
        <h2
          id="cohort-roster-heading"
          tabIndex={-1}
          className={cn(
            "text-[19px] text-[var(--cohort-ink)]",
            mobileTitle ? "text-[21px] lg:text-xl" : typeof previewLimit === "number" ? "sm:text-[22px]" : "sm:text-xl",
          )}
        >
          {mobileTitle ? <><span className="lg:hidden">{mobileTitle}</span><span className="hidden lg:inline">{title}</span></> : title}
        </h2>
        {onOpenAll ? (
          <div className="flex items-center gap-4">
            <div className="relative hidden min-w-[260px] sm:block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--cohort-ink-muted)]" />
              <Input placeholder="Search by name or role" className="min-h-10 rounded-xl border-[var(--cohort-rule)] bg-white pl-10 shadow-none" value={searchValue} onChange={(event) => onSearchChange(event.target.value)} />
            </div>
            <button type="button" onClick={onOpenAll} className="cohort-text-action min-h-11 text-sm">See all {allVisibleRoster.length}</button>
          </div>
        ) : <span className="text-sm text-[var(--cohort-ink-muted)]">{allVisibleRoster.length} people</span>}
      </div>
      {typeof previewLimit !== "number" ? (
        <div className="relative my-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--cohort-ink-muted)]" />
          <Input
            placeholder="Search by name or role"
            className="min-h-11 rounded-xl border-[var(--cohort-rule)] bg-white pl-10 shadow-none"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      ) : null}
      <div className={cn("cohort-bounded-list w-full", typeof previewLimit === "number" && "sm:grid sm:grid-cols-2 sm:gap-x-10")} role="region" aria-label={`${title} list`}>
        {allVisibleRoster.length === 0
          ? searchValue.trim()
            ? (
              <div className="py-8 text-center">
                <p className="text-sm text-[var(--cohort-ink-muted)]">No people match “{searchValue.trim()}”.</p>
                <button type="button" onClick={() => onSearchChange("")} className="cohort-text-action mt-2 min-h-11 text-sm">Clear search</button>
              </div>
            )
            : emptyState || <p className="py-5 text-sm text-[var(--cohort-ink-muted)]">No people are in this roster yet.</p>
          : groupByRole
            ? roleGroups.map((group, groupIndex) => (
                <div key={group.key}>
                  <p className={cn(
                    "cohort-roster-group-label pb-1 text-xs font-medium uppercase tracking-[0.06em] text-[var(--cohort-ink-muted)]",
                    groupIndex === 0 ? "pt-2" : "pt-4",
                  )}>
                    {group.label}
                  </p>
                  {group.people.map((person) => renderPersonRow(person))}
                </div>
              ))
            : visibleRoster.map((person, index) => renderPersonRow(person, index))}
      </div>
    </section>
  )
}
